import {
  Label,
  Option,
  Select,
  Sizes,
  Text,
} from '@sede-enterprise/shell-ds-react-framework';
import { SiteAvailabilityControl } from '@see-power-pft-common-js/net-availability/enums/site-availability-control';
import { SiteLimitForNetAvailability } from '@see-power-pft-common-js/net-availability/types';
import { NoSiteFound } from 'components/SiteSelector/NoSiteFound';
import { DYNATRACE_ACTIONS } from 'constants/dynatrace-actions';
import { getLatestSiteLimit } from 'pages/Availability/common/utils';
import { Asset } from 'pages/Availability/types';
import { useEffect, useMemo, useState } from 'react';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import { useStore } from 'store';
import { dynatraceTrackInstantAction } from 'utils/dynatrace';
import { extractFirstSegmentFromUrl } from 'utils/url';

type SiteType = {
  id: string;
  name: string;
  sourceName?: string;
  sourceId?: string;
  siteLimits?: SiteLimitForNetAvailability[];
  assets?: Asset[];
  siteAvailabilityControl: SiteAvailabilityControl;
};

const SiteSelector = ({
  sites,
  showLimits = false,
  disabled = false,
  isAvailabilitySiteSelector = false,
}: {
  sites: Array<SiteType>;
  showLimits?: boolean;
  disabled?: boolean;
  isAvailabilitySiteSelector?: boolean;
}) => {
  const { company, site } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchValue, setSearchValue] = useState<string>('');

  const [selectedItem, setSelectedItem] = useState<string>(() => site ?? '');

  const setSelectedSiteData = useStore((state) => state.setSelectedSiteData);

  const displayedSites = useMemo(() => {
    const filtered = searchValue
      ? sites.filter((x) =>
          x.name?.toLowerCase().includes(searchValue?.toLowerCase()),
        )
      : sites;

    return filtered.slice(0, 50);
  }, [searchValue, sites]);

  const siteOptions = useMemo(
    () =>
      displayedSites?.map((edge: SiteType) => {
        const latestSiteLimit = getLatestSiteLimit(edge.siteLimits ?? []);

        return {
          label: edge.name,
          value: String(edge.id),
          powerLimit: latestSiteLimit?.maxExportPower,
          maxCapacity: latestSiteLimit?.energyCapacity,
        };
      }) || [],
    [displayedSites],
  );

  useEffect(() => {
    setSelectedItem(site ?? '');
    setSearchValue('');
  }, [site, company]);

  // Update Availability store selection when the local selected item changes
  useEffect(() => {
    if (!isAvailabilitySiteSelector) return;
    if (!selectedItem || sites.length === 0) return;

    const selectedSite = sites.find((s) => s.id == selectedItem);
    if (!selectedSite) return;

    setSelectedSiteData({
      ...selectedSite,
      sourceId: selectedSite.sourceId ?? '',
      sourceName: selectedSite.sourceName ?? '',
      siteLimits: selectedSite.siteLimits ?? [],
      assets: selectedSite.assets ?? [],
      siteAvailabilityControl: selectedSite.siteAvailabilityControl,
    });
  }, [selectedItem, sites, isAvailabilitySiteSelector, setSelectedSiteData]);

  const handleSelect = async (site: string) => {
    dynatraceTrackInstantAction(DYNATRACE_ACTIONS.SWITCH_SITE);
    const path = generatePath(':company/:route/:site', {
      company: company ?? null,
      route: extractFirstSegmentFromUrl(location.pathname),
      site,
    });

    setSelectedItem(site);
    await navigate('/' + path);
    setSearchValue('');
  };

  const handleSearch = (e: string) => {
    setSearchValue(e);
  };

  const siteLimitsLabel = (
    name: string,
    powerLimit: number,
    maxCapacity: number,
  ) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <Text size={Sizes.Small}>{name}</Text>
      <Text size={Sizes.Small}>
        {powerLimit}MW / {maxCapacity}MWh
      </Text>
    </div>
  );

  return (
    <div
      style={{
        width: showLimits ? '400px' : '259px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
      }}
    >
      <Label style={{ color: '#757575' }}>Site</Label>
      <Select
        key={`pluto-site-selector-${company}`}
        data-testid={'pluto-site-selector'}
        allowClear={false}
        disabled={disabled}
        size={Sizes.Medium}
        optionLabelProp="label"
        value={selectedItem}
        searchValue={searchValue}
        onSelect={handleSelect}
        onSearch={handleSearch}
        filterOption={false}
        notFoundContent={<NoSiteFound />}
      >
        {siteOptions.map(({ value, label, powerLimit, maxCapacity }: any) => (
          <Option key={value} value={value} label={label}>
            <>
              {showLimits && powerLimit && maxCapacity
                ? siteLimitsLabel(label, powerLimit, maxCapacity)
                : label}
            </>
          </Option>
        ))}
      </Select>
    </div>
  );
};

export default SiteSelector;
